import type { CarrierEnabledScope, SiteCarrierStatus } from '@klareospace/pharos-lib-types';
import { SITE_CARRIER_STATUS } from '@klareospace/pharos-lib-types';

export type { SiteCarrierStatus };

interface Validity {
  from: Date | null | undefined;
  to: Date | null | undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts validity dates for a specific site within a carrier's enabled scopes.
 * Falls back to the space-level scope dates if no site-specific entry exists.
 */
export function findValidity(
  carrier: { enabledScopes: CarrierEnabledScope[] },
  spaceId: string,
  siteId: string,
): Validity | undefined {
  const scope = carrier.enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) return undefined;
  const siteEntry = scope.siteSpecifics?.find((ss) => ss.siteId === siteId);
  if (siteEntry) return { from: siteEntry.from, to: siteEntry.to };
  return { from: scope.from, to: scope.to };
}

/**
 * Computes the display status from a Validity object.
 * Returns 'enabled' if validity is undefined (shouldn't happen in practice).
 */
export function computeSiteCarrierStatus(validity: Validity | undefined): SiteCarrierStatus {
  if (!validity) return SITE_CARRIER_STATUS.ENABLED;
  const now = new Date();
  const { from, to } = validity;
  if (to && new Date(to) < now) return SITE_CARRIER_STATUS.EXPIRED;
  if (from && new Date(from) > now) return SITE_CARRIER_STATUS.SCHEDULED;
  return SITE_CARRIER_STATUS.ENABLED;
}
